import { useEffect, useRef, useState } from "react";
import {
  AxesHelper,
  Box3,
  CanvasTexture,
  GridHelper,
  Group,
  LineBasicMaterial,
  type Object3D,
  PerspectiveCamera,
  Raycaster,
  Scene,
  Sprite,
  SpriteMaterial,
  SRGBColorSpace,
  Vector2,
  Vector3,
  WebGLRenderer
} from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { modelAssetLibrary } from "../models/model-asset-library";
import { ModelAsset } from "../models/model-assets";
import { disposeObject3DResources as disposeRenderableObject } from "../models/shared-model-resources";
import { BinRack, type BinRackOptions } from "../objects/bin-rack";
import { DoubleChainConveyor } from "../objects/double-chain-conveyor";
import { PalletReciprocatingLift } from "../objects/pallet-reciprocating-lift";
import { PalletStackerCrane } from "../objects/pallet-stacker-crane";
import { RackShelf } from "../objects/rack-shelf";
import { RollerConveyor } from "../objects/roller-conveyor";
import { RollerLiftTransfer } from "../objects/roller-lift-transfer";
import { SingleStationReciprocatingShuttle } from "../objects/single-station-reciprocating-shuttle";
import {
  type CargoCellSize,
  defaultStandardRackShelfCargoCellSize,
  StandardRackShelf,
  type StandardRackShelfOptions
} from "../objects/standard-rack-shelf";
import { createFactoryLighting } from "../scenes/factory-lighting";
import { DoubleChainConveyorControls } from "./double-chain-conveyor-controls";
import { PalletReciprocatingLiftControls } from "./pallet-reciprocating-lift-controls";
import { PalletStackerCraneControls } from "./pallet-stacker-crane-controls";
import { RackShelfCellSizeControls } from "./rack-shelf-cell-size-controls";
import { RollerConveyorControls } from "./roller-conveyor-controls";
import { RollerLiftTransferControls } from "./roller-lift-transfer-controls";
import { SceneLoading, type SceneLoadingState } from "./scene-loading";
import { SingleStationReciprocatingShuttleControls } from "./single-station-reciprocating-shuttle-controls";

const initialCrossbarZOffset = 0.0185;
const binRackSceneOptions: BinRackOptions = {
  height: 4,
  heightCellCount: 4,
  length: 5.4,
  lengthCellCount: 3,
  position: { x: -2.7, y: 0, z: 8 },
  width: 0.87
};
const doubleChainConveyorSceneOptions = {
  position: { x: 0, y: 0, z: 0 }
};
const rollerConveyorSceneOptions = {
  position: { x: 3, y: 0, z: 8 },
  sideRailEndInset: 0.02,
  sideRailXInset: 0.07,
  sideRailYOffset: -0.11
};
const rollerLiftTransferSceneOptions = {
  height: 0.4,
  position: { x: 6, z: 8 }
};
const palletReciprocatingLiftSceneOptions = {
  bottomSideRailInset: 0.25,
  layerCount: 4,
  position: { x: 7, y: 0, z: 0 }
};
const singleStationReciprocatingShuttleSceneOptions = {
  height: 0.4,
  length: 5,
  position: { x: 10, y: 0, z: 8 }
};
const palletStackerCraneSceneOptions = {
  height: 8,
  length: 12,
  position: { x: 0, y: 0, z: 13 }
};
type RackShelfSceneConfig = StandardRackShelfOptions & {
  heightCellCount: number;
  lengthCellCount: number;
};
const rackShelfSceneConfigs: RackShelfSceneConfig[] = [
  {
    bottomBraceY: 0.5,
    heightCellCount: 4,
    lengthCellCount: 3,
    position: { x: -7, y: 0, z: -4 },
    topBraceInset: 0.5
  },
  {
    bottomBraceY: 0.5,
    heightCellCount: 3,
    lengthCellCount: 4,
    position: { x: 2, y: 0, z: -4 },
    topBraceInset: 0.5
  },
  {
    bottomBraceY: 0.5,
    heightCellCount: 5,
    lengthCellCount: 2,
    position: { x: -7, y: 0, z: 3 },
    topBraceInset: 0.5
  },
  {
    bottomBraceY: 0.5,
    heightCellCount: 2,
    lengthCellCount: 3,
    position: { x: 2, y: 0, z: 3 },
    topBraceInset: 0.5
  }
];

type SceneLoadState = SceneLoadingState | "ready";
type CargoSceneModels = {
  binRack: BinRack;
  doubleChainConveyor: DoubleChainConveyor;
  palletReciprocatingLift: PalletReciprocatingLift;
  rackShelves: StandardRackShelf[];
  rollerConveyor: RollerConveyor;
  rollerLiftTransfer: RollerLiftTransfer;
  singleStationReciprocatingShuttle: SingleStationReciprocatingShuttle;
  palletStackerCrane: PalletStackerCrane;
};
type CargoSceneController = {
  applyCargoCellSize: (cargoCellSize: CargoCellSize) => Promise<void>;
  dispose: () => void;
  setConveyorCrossbarZInset: (crossbarZInset: number) => void;
  setConveyorSideRailEndInset: (sideRailEndInset: number) => void;
  setConveyorSideRailXInset: (sideRailXInset: number) => void;
  setConveyorSideRailYOffset: (sideRailYOffset: number) => void;
  setConveyorWheelXOutset: (wheelXOutset: number) => void;
  setPalletReciprocatingLiftLayerCount: (layerCount: number) => void;
  setPalletReciprocatingLiftBottomSideRailInset: (bottomSideRailInset: number) => void;
  setRollerConveyorSideRailEndInset: (sideRailEndInset: number) => void;
  setRollerConveyorSideRailXInset: (sideRailXInset: number) => void;
  setRollerConveyorSideRailYOffset: (sideRailYOffset: number) => void;
  setRollerLiftTransferHeight: (height: number) => void;
  setSingleStationReciprocatingShuttleHeight: (height: number) => void;
  setPalletStackerCraneHeight: (height: number) => void;
};

export default function CargoTestScene() {
  const hostRef = useRef<HTMLDivElement>(null);
  const sceneControllerRef = useRef<CargoSceneController | undefined>(undefined);
  const [applyingCargoCellSize, setApplyingCargoCellSize] = useState(false);
  const [loadState, setLoadState] = useState<SceneLoadState>("loading");

  useEffect(() => {
    const host = hostRef.current;

    if (!host) {
      return;
    }

    let sceneController: CargoSceneController | undefined;
    let disposed = false;

    setLoadState("loading");
    void loadCargoSceneModels(initialCrossbarZOffset)
      .then((sceneModels) => {
        if (disposed) {
          disposeRenderableObject(sceneModels.binRack);
          disposeRenderableObject(sceneModels.doubleChainConveyor);
          disposeRenderableObject(sceneModels.palletReciprocatingLift);
          disposeRenderableObject(sceneModels.rollerConveyor);
          disposeRenderableObject(sceneModels.rollerLiftTransfer);
          disposeRenderableObject(sceneModels.singleStationReciprocatingShuttle);
          disposeRenderableObject(sceneModels.palletStackerCrane);
          sceneModels.rackShelves.forEach((shelf) => {
            disposeRenderableObject(shelf);
          });
          return;
        }

        sceneController = startCargoScene(host, sceneModels);
        sceneControllerRef.current = sceneController;
        setLoadState("ready");
      })
      .catch((error: unknown) => {
        if (!disposed) {
          console.error("Failed to load rack shelves", error);
          setLoadState("error");
        }
      });

    return () => {
      disposed = true;
      if (sceneControllerRef.current === sceneController) {
        sceneControllerRef.current = undefined;
      }
      sceneController?.dispose();
    };
  }, []);

  const applyCargoCellSize = async (cargoCellSize: CargoCellSize) => {
    const sceneController = sceneControllerRef.current;

    if (!sceneController) {
      return;
    }

    setApplyingCargoCellSize(true);
    try {
      await sceneController.applyCargoCellSize(cargoCellSize);
    } catch (error: unknown) {
      console.error("Failed to update cargo cell size", error);
    } finally {
      if (sceneControllerRef.current === sceneController) {
        setApplyingCargoCellSize(false);
      }
    }
  };

  const setConveyorSideRailYOffset = (sideRailYOffset: number) => {
    sceneControllerRef.current?.setConveyorSideRailYOffset(sideRailYOffset);
  };

  const setConveyorSideRailXInset = (sideRailXInset: number) => {
    sceneControllerRef.current?.setConveyorSideRailXInset(sideRailXInset);
  };

  const setConveyorSideRailEndInset = (sideRailEndInset: number) => {
    sceneControllerRef.current?.setConveyorSideRailEndInset(sideRailEndInset);
  };

  const setConveyorCrossbarZInset = (crossbarZInset: number) => {
    sceneControllerRef.current?.setConveyorCrossbarZInset(crossbarZInset);
  };

  const setConveyorWheelXOutset = (wheelXOutset: number) => {
    sceneControllerRef.current?.setConveyorWheelXOutset(wheelXOutset);
  };

  const setRollerConveyorSideRailYOffset = (sideRailYOffset: number) => {
    sceneControllerRef.current?.setRollerConveyorSideRailYOffset(sideRailYOffset);
  };

  const setRollerConveyorSideRailEndInset = (sideRailEndInset: number) => {
    sceneControllerRef.current?.setRollerConveyorSideRailEndInset(sideRailEndInset);
  };

  const setRollerConveyorSideRailXInset = (sideRailXInset: number) => {
    sceneControllerRef.current?.setRollerConveyorSideRailXInset(sideRailXInset);
  };

  const setRollerLiftTransferHeight = (height: number) => {
    sceneControllerRef.current?.setRollerLiftTransferHeight(height);
  };

  const setPalletReciprocatingLiftLayerCount = (layerCount: number) => {
    sceneControllerRef.current?.setPalletReciprocatingLiftLayerCount(layerCount);
  };

  const setPalletReciprocatingLiftBottomSideRailInset = (bottomSideRailInset: number) => {
    sceneControllerRef.current?.setPalletReciprocatingLiftBottomSideRailInset(bottomSideRailInset);
  };

  const setSingleStationReciprocatingShuttleHeight = (height: number) => {
    sceneControllerRef.current?.setSingleStationReciprocatingShuttleHeight(height);
  };
  const setPalletStackerCraneHeight = (height: number) => {
    sceneControllerRef.current?.setPalletStackerCraneHeight(height);
  };

  return (
    <div className="scene-stage">
      <div className="scene-host" ref={hostRef} />
      <div className="cargo-test-scene-controls">
        <RackShelfCellSizeControls
          disabled={loadState !== "ready" || applyingCargoCellSize}
          initialValue={defaultStandardRackShelfCargoCellSize}
          onApply={applyCargoCellSize}
        />
        <DoubleChainConveyorControls
          disabled={loadState !== "ready"}
          initialCrossbarZInset={0.004}
          initialEndInset={0.02}
          initialXInset={0.156}
          initialYOffset={0.26}
          initialWheelXOutset={0.06}
          onCrossbarZInsetChange={setConveyorCrossbarZInset}
          onEndInsetChange={setConveyorSideRailEndInset}
          onXInsetChange={setConveyorSideRailXInset}
          onYOffsetChange={setConveyorSideRailYOffset}
          onWheelXOutsetChange={setConveyorWheelXOutset}
        />
        <RollerConveyorControls
          disabled={loadState !== "ready"}
          initialEndInset={rollerConveyorSceneOptions.sideRailEndInset}
          initialXInset={rollerConveyorSceneOptions.sideRailXInset}
          initialYOffset={rollerConveyorSceneOptions.sideRailYOffset}
          onEndInsetChange={setRollerConveyorSideRailEndInset}
          onXInsetChange={setRollerConveyorSideRailXInset}
          onYOffsetChange={setRollerConveyorSideRailYOffset}
        />
        <RollerLiftTransferControls
          disabled={loadState !== "ready"}
          initialHeight={rollerLiftTransferSceneOptions.height}
          onHeightChange={setRollerLiftTransferHeight}
        />
        <PalletReciprocatingLiftControls
          disabled={loadState !== "ready"}
          initialBottomSideRailInset={palletReciprocatingLiftSceneOptions.bottomSideRailInset}
          initialLayerCount={palletReciprocatingLiftSceneOptions.layerCount}
          onBottomSideRailInsetChange={setPalletReciprocatingLiftBottomSideRailInset}
          onLayerCountChange={setPalletReciprocatingLiftLayerCount}
        />
        <SingleStationReciprocatingShuttleControls
          disabled={loadState !== "ready"}
          initialHeight={singleStationReciprocatingShuttleSceneOptions.height}
          onHeightChange={setSingleStationReciprocatingShuttleHeight}
        />
        <PalletStackerCraneControls
          disabled={loadState !== "ready"}
          initialHeight={palletStackerCraneSceneOptions.height}
          onHeightChange={setPalletStackerCraneHeight}
        />
      </div>
      {loadState !== "ready" ? <SceneLoading state={loadState} /> : null}
    </div>
  );
}

function startCargoScene(
  host: HTMLDivElement,
  {
    binRack,
    doubleChainConveyor,
    palletReciprocatingLift,
    rackShelves,
    rollerConveyor,
    rollerLiftTransfer,
    singleStationReciprocatingShuttle,
    palletStackerCrane
  }: CargoSceneModels
) {
  const scene = new Scene();

  const renderer = new WebGLRenderer({ antialias: true, alpha: false });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(host.clientWidth, host.clientHeight);
  renderer.outputColorSpace = SRGBColorSpace;
  renderer.localClippingEnabled = true;
  host.appendChild(renderer.domElement);

  const camera = new PerspectiveCamera(
    45,
    host.clientWidth / Math.max(host.clientHeight, 1),
    0.01,
    1000
  );
  camera.position.set(8, 6, 8);

  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.08;
  controls.enablePan = true;
  controls.enableZoom = true;
  controls.target.set(0, 0, 0);
  controls.update();

  const grid = new GridHelper(24, 48, "#ffffff", "#ffffff");
  grid.position.y = 0.002;

  const gridMaterials = Array.isArray(grid.material) ? grid.material : [grid.material];
  gridMaterials.forEach((material) => {
    if (material instanceof LineBasicMaterial) {
      material.transparent = true;
      material.opacity = 0.18;
      material.depthWrite = false;
    }
  });
  scene.add(grid);

  const axes = new AxesHelper(4);
  scene.add(axes);

  const axisLabels = [
    createAxisLabel("X", "#ff4d4d", new Vector3(4.4, 0, 0)),
    createAxisLabel("Y", "#6dff6d", new Vector3(0, 4.4, 0)),
    createAxisLabel("Z", "#5d8cff", new Vector3(0, 0, 4.4))
  ];
  scene.add(...axisLabels);

  const modelGroup = new Group();
  modelGroup.add(
    ...rackShelves,
    binRack,
    doubleChainConveyor,
    rollerConveyor,
    rollerLiftTransfer,
    palletReciprocatingLift,
    singleStationReciprocatingShuttle,
    palletStackerCrane
  );
  scene.add(modelGroup);
  const lighting = createFactoryLighting(scene, renderer, modelGroup);

  const raycaster = new Raycaster();
  const pointer = new Vector2();
  let disposed = false;
  let hoveredCargoCellTarget: Object3D | undefined;
  let hoveredRackShelf: RackShelf | undefined;
  let selectedRackShelf: RackShelf | undefined;
  let updateQueue: Promise<void> = Promise.resolve();

  const clearCargoCellHover = () => {
    hoveredRackShelf?.hideCargoCellHighlight();
    hoveredRackShelf = undefined;
    hoveredCargoCellTarget = undefined;
    renderer.domElement.style.cursor = "";
  };

  const findCargoCellHit = (event: MouseEvent | PointerEvent) => {
    const bounds = renderer.domElement.getBoundingClientRect();

    if (bounds.width <= 0 || bounds.height <= 0) {
      return;
    }

    pointer.x = ((event.clientX - bounds.left) / bounds.width) * 2 - 1;
    pointer.y = -(((event.clientY - bounds.top) / bounds.height) * 2 - 1);
    raycaster.setFromCamera(pointer, camera);

    const intersections = raycaster.intersectObjects(
      rackShelves.flatMap((shelf) => shelf.getCargoCellHitTargets()),
      false
    );
    const hitTarget = intersections[0]?.object;
    const hitShelf = hitTarget?.userData.rackShelf;

    if (!hitTarget || !(hitShelf instanceof RackShelf)) {
      return;
    }

    return { hitShelf, hitTarget };
  };

  const handlePointerMove = (event: PointerEvent) => {
    const hit = findCargoCellHit(event);

    if (!hit) {
      clearCargoCellHover();
      return;
    }

    if (hoveredRackShelf === hit.hitShelf && hoveredCargoCellTarget === hit.hitTarget) {
      return;
    }

    hoveredRackShelf?.hideCargoCellHighlight();
    hit.hitShelf.showCargoCellHighlight(hit.hitTarget);
    hoveredRackShelf = hit.hitShelf;
    hoveredCargoCellTarget = hit.hitTarget;
    renderer.domElement.style.cursor = "pointer";
  };

  const handleDoubleClick = (event: MouseEvent) => {
    const hit = findCargoCellHit(event);

    if (!hit) {
      return;
    }

    selectedRackShelf?.setSelected(false);
    hit.hitShelf.setSelected(true);
    hit.hitShelf.selectCargoCell(hit.hitTarget);
    selectedRackShelf = hit.hitShelf;
  };

  const handlePointerLeave = () => {
    clearCargoCellHover();
  };

  renderer.domElement.addEventListener("pointermove", handlePointerMove);
  renderer.domElement.addEventListener("dblclick", handleDoubleClick);
  renderer.domElement.addEventListener("pointerleave", handlePointerLeave);

  frameCameraToObject(camera, controls, modelGroup, host.clientWidth, host.clientHeight);

  const resizeObserver = new ResizeObserver(() => {
    const width = Math.max(host.clientWidth, 1);
    const height = Math.max(host.clientHeight, 1);
    renderer.setSize(width, height);
    camera.aspect = width / height;
    camera.updateProjectionMatrix();
  });
  resizeObserver.observe(host);

  const render = () => {
    controls.update();
    renderer.render(scene, camera);
    frameId = window.requestAnimationFrame(render);
  };

  renderer.render(scene, camera);
  let frameId = window.requestAnimationFrame(render);

  return {
    applyCargoCellSize(cargoCellSize: CargoCellSize) {
      const nextUpdate = updateQueue.then(async () => {
        if (disposed) {
          return;
        }

        clearCargoCellHover();
        await Promise.all(
          rackShelves.map((rackShelf) =>
            rackShelf.setCargoCellSize(cargoCellSize, modelAssetLibrary)
          )
        );

        if (!disposed) {
          lighting.refit(modelGroup);
          frameCameraToObject(camera, controls, modelGroup, host.clientWidth, host.clientHeight);
        }
      });

      updateQueue = nextUpdate.catch(() => undefined);
      return nextUpdate;
    },
    dispose() {
      if (disposed) {
        return;
      }

      disposed = true;
      resizeObserver.disconnect();
      window.cancelAnimationFrame(frameId);
      renderer.domElement.removeEventListener("pointermove", handlePointerMove);
      renderer.domElement.removeEventListener("dblclick", handleDoubleClick);
      renderer.domElement.removeEventListener("pointerleave", handlePointerLeave);
      controls.dispose();
      void updateQueue.finally(() => {
        disposeRenderableObject(modelGroup);
      });
      grid.geometry.dispose();
      gridMaterials.forEach((material) => {
        material.dispose();
      });
      axes.geometry.dispose();
      if (Array.isArray(axes.material)) {
        axes.material.forEach((material) => {
          material.dispose();
        });
      } else {
        axes.material.dispose();
      }
      axisLabels.forEach((label) => {
        label.material.map?.dispose();
        label.material.dispose();
      });
      lighting.dispose();
      renderer.dispose();
      renderer.domElement.remove();
    },
    setConveyorSideRailYOffset(sideRailYOffset: number) {
      doubleChainConveyor.setSideRailYOffset(sideRailYOffset);
      lighting.refit(modelGroup);
    },
    setConveyorSideRailXInset(sideRailXInset: number) {
      doubleChainConveyor.setSideRailXInset(sideRailXInset);
      lighting.refit(modelGroup);
    },
    setConveyorSideRailEndInset(sideRailEndInset: number) {
      doubleChainConveyor.setSideRailEndInset(sideRailEndInset);
      lighting.refit(modelGroup);
    },
    setConveyorCrossbarZInset(crossbarZInset: number) {
      doubleChainConveyor.setCrossbarZInset(crossbarZInset);
      lighting.refit(modelGroup);
    },
    setConveyorWheelXOutset(wheelXOutset: number) {
      doubleChainConveyor.setWheelXOutset(wheelXOutset);
      lighting.refit(modelGroup);
    },
    setRollerConveyorSideRailXInset(sideRailXInset: number) {
      rollerConveyor.setSideRailXInset(sideRailXInset);
      lighting.refit(modelGroup);
    },
    setRollerConveyorSideRailEndInset(sideRailEndInset: number) {
      rollerConveyor.setSideRailEndInset(sideRailEndInset);
      lighting.refit(modelGroup);
    },
    setRollerConveyorSideRailYOffset(sideRailYOffset: number) {
      rollerConveyor.setSideRailYOffset(sideRailYOffset);
      lighting.refit(modelGroup);
    },
    setRollerLiftTransferHeight(height: number) {
      rollerLiftTransfer.setHeight(height);
      lighting.refit(modelGroup);
    },
    setPalletReciprocatingLiftLayerCount(layerCount: number) {
      palletReciprocatingLift.setLayerCount(layerCount);
      lighting.refit(modelGroup);
    },
    setPalletReciprocatingLiftBottomSideRailInset(bottomSideRailInset: number) {
      palletReciprocatingLift.setBottomSideRailInset(bottomSideRailInset);
      lighting.refit(modelGroup);
    },
    setSingleStationReciprocatingShuttleHeight(height: number) {
      singleStationReciprocatingShuttle.setHeight(height);
      lighting.refit(modelGroup);
    },
    setPalletStackerCraneHeight(height: number) {
      palletStackerCrane.setHeight(height);
      lighting.refit(modelGroup);
    }
  };
}

function createAxisLabel(text: string, color: string, position: Vector3) {
  const canvas = document.createElement("canvas");
  canvas.width = 128;
  canvas.height = 128;

  const context = canvas.getContext("2d");

  if (context) {
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.fillStyle = "rgba(0, 0, 0, 0.62)";
    context.beginPath();
    context.roundRect(14, 14, 100, 100, 18);
    context.fill();
    context.strokeStyle = color;
    context.lineWidth = 6;
    context.stroke();
    context.fillStyle = color;
    context.font = "bold 72px sans-serif";
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.fillText(text, 64, 68);
  }

  const texture = new CanvasTexture(canvas);
  texture.colorSpace = SRGBColorSpace;

  const sprite = new Sprite(
    new SpriteMaterial({
      map: texture,
      transparent: true
    })
  );
  sprite.position.copy(position);
  sprite.scale.set(0.62, 0.62, 0.62);
  return sprite;
}

async function loadCargoSceneModels(crossbarZOffset: number) {
  await modelAssetLibrary.preload();
  const [
    rackShelves,
    binRack,
    doubleChainConveyor,
    rollerConveyor,
    rollerLiftTransfer,
    palletReciprocatingLift,
    singleStationReciprocatingShuttle,
    palletStackerCrane
  ] = await Promise.all([
    loadRackShelves(crossbarZOffset),
    BinRack.create(binRackSceneOptions, modelAssetLibrary),
    DoubleChainConveyor.create(doubleChainConveyorSceneOptions, modelAssetLibrary),
    RollerConveyor.create(rollerConveyorSceneOptions, modelAssetLibrary),
    RollerLiftTransfer.create(rollerLiftTransferSceneOptions, modelAssetLibrary),
    PalletReciprocatingLift.create(palletReciprocatingLiftSceneOptions, modelAssetLibrary),
    SingleStationReciprocatingShuttle.create(
      singleStationReciprocatingShuttleSceneOptions,
      modelAssetLibrary
    ),
    PalletStackerCrane.create(palletStackerCraneSceneOptions, modelAssetLibrary)
  ]);

  await Promise.all(
    rackShelves.flatMap((rackShelf, index) => {
      const config = rackShelfSceneConfigs[index];
      return [
        setRackShelfCargoLayer(
          rackShelf,
          config.lengthCellCount,
          config.heightCellCount - 1,
          ModelAsset.WoodenCrate
        ),
        setRackShelfCargoLayer(
          rackShelf,
          config.lengthCellCount,
          config.heightCellCount - 2,
          ModelAsset.SteelCoil
        )
      ];
    })
  );

  return {
    binRack,
    doubleChainConveyor,
    palletReciprocatingLift,
    rackShelves,
    rollerConveyor,
    rollerLiftTransfer,
    singleStationReciprocatingShuttle,
    palletStackerCrane
  };
}

async function loadRackShelves(crossbarZOffset: number) {
  return Promise.all(
    rackShelfSceneConfigs.map((config) =>
      StandardRackShelf.create(
        {
          ...config,
          crossbarZOffset
        },
        modelAssetLibrary
      )
    )
  );
}

async function setRackShelfCargoLayer(
  rackShelf: StandardRackShelf,
  lengthCellCount: number,
  heightIndex: number,
  asset: ModelAsset
) {
  await Promise.all(
    Array.from({ length: lengthCellCount }, (_, lengthIndex) =>
      Promise.all(
        Array.from({ length: rackShelf.getConfiguration().cargoCapacity }, async (_, slotIndex) => {
          const cargo = await modelAssetLibrary.load(asset);
          cargo.name = `rack-shelf-${asset}-${heightIndex}-${lengthIndex}-${slotIndex}`;
          rackShelf.setCargo({ heightIndex, lengthIndex, slotIndex }, cargo);
        })
      )
    )
  );
}

function frameCameraToObject(
  camera: PerspectiveCamera,
  controls: OrbitControls,
  object: Group,
  width: number,
  height: number
) {
  object.updateWorldMatrix(true, true);
  const bounds = new Box3().setFromObject(object);
  const size = bounds.getSize(new Vector3());
  const center = bounds.getCenter(new Vector3());
  const maxSize = Math.max(size.x, size.y, size.z, 1);
  const fitHeightDistance = maxSize / (2 * Math.tan((Math.PI * camera.fov) / 360));
  const fitWidthDistance = fitHeightDistance / Math.max(width / height, 0.1);
  const distance = Math.max(fitHeightDistance, fitWidthDistance) * 1.65;

  controls.target.copy(center);
  camera.position.copy(center).add(new Vector3(distance, distance * 0.55, distance));
  camera.near = Math.max(distance / 100, 0.01);
  camera.far = distance * 100;
  camera.updateProjectionMatrix();
  controls.update();
}
